import * as moment from 'moment';
import { Injectable } from '@angular/core';
import { State, Action, StateContext, Store, Selector } from '@ngxs/store';
import { concat, defer, iif, merge, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import produce from 'immer';
import { uniq, uniqueId } from 'lodash';
import {
  mapReorderDetails,
  Provider,
  mapProvider,
  mapProviderVisitVerification,
  mapProviderVisit,
  ReorderDetailsItem,
  mapDaysOnHandRequired,
  mapFrequenciesRequired,
  mapCopay,
  mapDatesOfService,
  buildDatesOfServiceRequest,
  DosResult,
  ApiProviderVisitsRequest,
  buildCopayRequest,
  mapProviderVisitValidationResponse,
  buildProviderVisitValidationRequest,
  mapCollectionStatus,
  OneDotOrderStatus,
} from '../../../models';
import { ApiService, EnvService, SentryService } from '../../../services';
import { PatientInsurancesStateModel } from '../../patient-insurances/patient-insurances.state';
import {
  defaultApiStoreData,
  defaultApiStoreIterableData,
  InvalidApiResponseDataError,
  MissingApiRequestDataError,
} from '../../utils';
import { uniqBy } from 'lodash-es';
import { FetchOrdersAndReorders } from '../../order/orders.actions';
import { OrdersState } from '../../order/orders.state';
import {
  RefetchDosAndCopayIfNeeded,
  RefetchCopayIfNeeded,
  LoadOrderWizard,
  SetInsuranceUserData,
  SetProductItemQuantitiesUserData,
  SetProductItemsDohUserData,
  SetProductItemsOuDohUserData,
  SetProductFrequenciesUserData,
  SetProviderUserData,
  ValidateProviderVisit,
  SetNewProviderUserData,
  SetContactsUserData,
  SetSelectedPaymentMethodUserData,
  ResetOrderWizardData,
  UpdateCopayWithDeductibleOverrides,
  SetDeductibleOverridesUserData,
  SetOrderConfirmed,
} from './order-wizard-data.actions';
import { FetchPatientPaymentMethods } from '../../payment-methods/payment-methods.actions';
import { FetchCopayOptions } from './order-wizard-data.model';
import { isEqual } from 'lodash-es';
import {
  COLLECT_PAYMENT_OPTIONAL_THRESHOLD_USD,
  COLLECT_PAYMENT_THRESHOLD_USD,
  OU_SHIPMENT_TYPE_IDS,
  SHIPPING_SOON_THRESHOLD_DAYS,
} from 'src/app/core/constants';
import {
  AddressType,
  DatesOfService,
  DaysOnHandRequired,
  FrequenciesRequired,
  mapInsuranceFormValues,
  mapProviderFormValues,
  PatientInsurance,
  ProviderVisitVerification,
  ProviderVisit,
  PatientAddress,
  PatientCopay,
  PaymentMethod,
  ShipmentCollectionStatus,
  CollectionStatus,
  DeductibleOverridesFormValues,
  ReorderDetails,
} from 'src/app/core/models';
import { AddressesState, AddressesStateModel } from '../../addresses/addresses.state';
import { LookupState, LookupStateModel } from '../../lookup/lookup.state';
import { OrdersStateModel } from '../../order/orders.state';
import { PatientInsurancesState } from '../../patient-insurances/patient-insurances.state';
import { PaymentMethodsState, PaymentMethodsStateModel } from '../../payment-methods/payment-methods.state';
import { ApiStoreData, CcsSentryError, hasUnknownOrInvalidData } from '../../utils';
import {
  ContactsUserData,
  ExitOrderWizardStatus,
  InsuranceUserData,
  OrderWizardDataStateModel,
  OrderWizardStatus,
  ProductFrequenciesUserData,
  ProductItemDohUserData,
  ProductItemQuantityUserData,
  ProductsUserData,
  ProviderUserData,
  SelectedProvider,
  OutOfPocketMetStatus,
} from './order-wizard-data.model';
import { PatientState } from '../../patient/patient.state';

const userDataDefaults = {
  insuranceStep: {
    primaryInsurance: null,
    secondaryInsurance: null,
  },
  productsStep: {
    quantities: null,
    dohValues: null,
    ouDohValue: null,
    frequencies: null,
  },
  providerStep: {
    selectedProviderId: null,
    providerVisitFormValues: {
      lastProviderVisit: null,
      hasUpcomingVisit: null,
      nextProviderVisit: null,
      isLastProviderVisitValid: null,
      isNextProviderVisitValid: null,
    },
    newProviderFormValues: null,
  },
  contactsStep: {
    selectedAddressId: null,
  },
  billingStep: {
    selectedPaymentMethodId: null,
    deductibleOverrides: null,
    submittedDeductibleOverrides: null,
    appliedDeductibleOverrides: null,
  },
  isOrderConfirmed: false,
};

const defaults = {
  reorder: defaultApiStoreData,
  datesOfService: defaultApiStoreData,
  daysOnHandRequired: defaultApiStoreIterableData,
  frequenciesRequired: defaultApiStoreData,
  providers: defaultApiStoreIterableData,
  providerVisitVerification: defaultApiStoreData,
  providerVisits: defaultApiStoreIterableData,
  collectionStatus: defaultApiStoreData,
  copay: defaultApiStoreData,
  paymentMethods: defaultApiStoreIterableData,
  userData: userDataDefaults,
  lastProviderVisitValidation: defaultApiStoreData,
  nextProviderVisitValidation: defaultApiStoreData,
  updateCopayDeductibleOverrides: defaultApiStoreData,
};

@State<OrderWizardDataStateModel>({
  name: 'orderWizardData',
  defaults,
})
@Injectable({ providedIn: 'root' })
export class OrderWizardDataState {
  constructor(
    private apiService: ApiService,
    private store: Store,
    private sentry: SentryService,
    private envService: EnvService
  ) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector([AddressesState, OrdersState])
  static reorder(
    state: OrderWizardDataStateModel,
    addressesState: AddressesStateModel,
    ordersState: OrdersStateModel
  ): ReorderDetails | null {
    const reorder = state.reorder.data;

    if (!reorder) return null;

    const foundReorder = ordersState.data.find((r) => r.id === reorder?.id);

    let userSelectedAddress = null;

    const userSelectedAddressId = state.userData.contactsStep.selectedAddressId;

    userSelectedAddress = userSelectedAddressId
      ? addressesState.addresses.data.find((a) => a.id === userSelectedAddressId)
      : null;

    return {
      ...reorder,
      shippingAddress:
        userSelectedAddress ||
        foundReorder?.shippingAddress ||
        addressesState.addresses.data.find((a) => a.types.includes(AddressType.Shipping)),
    };
  }

  @Selector()
  static reorderItemsSummary(state: OrderWizardDataStateModel): (ReorderDetailsItem & ProductItemQuantityUserData)[] {
    const reorderItems = state.reorder.data?.items || [];
    const quantitiesUserData = state.userData.productsStep.quantities;

    return reorderItems.map((item) => {
      const itemQuantityUserData = quantitiesUserData?.find((qud) => qud.itemUiId === item.uiId) || {
        itemUiId: item.uiId,
        initialQuantity: item.quantity || 0,
        userQuantity: item.quantity || 0,
        maxQuantity: item.maxValidShippingQuantity,
        quantityToSubmit: item.quantity || 0,
        warningTitle: null,
        warningMessage: null,
      };

      return {
        ...item,
        ...itemQuantityUserData,
      };
    });
  }

  @Selector()
  static datesOfService(state: OrderWizardDataStateModel): DatesOfService | null {
    return state.datesOfService.data;
  }

  @Selector()
  static daysOnHandRequired(state: OrderWizardDataStateModel): DaysOnHandRequired[] {
    return state.daysOnHandRequired.data;
  }

  @Selector()
  static frequenciesRequired(state: OrderWizardDataStateModel): FrequenciesRequired | null {
    return state.frequenciesRequired.data;
  }

  @Selector()
  static isOuShipment(state: OrderWizardDataStateModel): boolean {
    const reorderShipmentTypeId = state.reorder.data?.shipmentTypeId;
    const isOu = !!reorderShipmentTypeId && OU_SHIPMENT_TYPE_IDS.includes(reorderShipmentTypeId!);

    return isOu;
  }

  @Selector()
  static hasInsuranceRequirements(state: OrderWizardDataStateModel): boolean {
    if (this.isOuShipment(state)) return true;

    const nonOuDaysOnHandRequired =
      state.daysOnHandRequired.data.filter((doh) => doh.daysOnHandRequired).length > 0 ||
      (state.frequenciesRequired.data ? Object.values(state.frequenciesRequired.data).some((v) => v) : false);

    return nonOuDaysOnHandRequired;
  }

  @Selector([LookupState, PatientInsurancesState])
  static insurance(
    state: OrderWizardDataStateModel,
    lookupState: LookupStateModel,
    patientInsurancesState: PatientInsurancesStateModel
  ): PatientInsurance {
    const relationshipsToInsured = lookupState.relationshipsToInsured.data;

    const primaryInsuranceUserData = state.userData.insuranceStep.primaryInsurance;
    const primaryInsurance = primaryInsuranceUserData
      ? mapInsuranceFormValues(primaryInsuranceUserData, relationshipsToInsured)
      : patientInsurancesState.insurance.data?.primaryInsurance;

    const secondaryInsuranceUserData = state.userData.insuranceStep.secondaryInsurance;
    const secondaryInsurance = secondaryInsuranceUserData
      ? mapInsuranceFormValues(secondaryInsuranceUserData, relationshipsToInsured)
      : patientInsurancesState.insurance.data?.secondaryInsurance;

    return {
      primaryInsurance,
      secondaryInsurance,
      isMedicareOrMedicareRepl: patientInsurancesState.insurance.data?.isMedicareOrMedicareRepl || false,
    };
  }

  @Selector()
  static providers(state: OrderWizardDataStateModel): Provider[] {
    const { newProviderFormValues } = state.userData.providerStep;

    if (newProviderFormValues) {
      return [mapProviderFormValues(newProviderFormValues), ...state.providers.data];
    }

    return state.providers.data;
  }

  @Selector()
  static providerVisitVerification(state: OrderWizardDataStateModel): ProviderVisitVerification | null {
    return state.providerVisitVerification.data;
  }

  @Selector()
  static providerVisits(state: OrderWizardDataStateModel): ProviderVisit[] {
    return state.providerVisits.data;
  }

  @Selector([AddressesState])
  static addresses(state: OrderWizardDataStateModel, addressesState: AddressesStateModel): PatientAddress[] {
    return addressesState.addresses.data;
  }

  @Selector()
  static collectionStatus(state: OrderWizardDataStateModel): ShipmentCollectionStatus | null {
    return state.collectionStatus.data;
  }

  @Selector()
  static copay(state: OrderWizardDataStateModel): PatientCopay | null {
    return state.copay.data;
  }

  @Selector()
  static updateCopayDeductibleOverridesState(state: OrderWizardDataStateModel): ApiStoreData<null> {
    return state.updateCopayDeductibleOverrides;
  }

  @Selector([PaymentMethodsState])
  static selectedPaymentMethod(
    state: OrderWizardDataStateModel,
    paymentMethodsState: PaymentMethodsStateModel
  ): PaymentMethod | null {
    const userSelectedPaymentMethodId = state.userData.billingStep.selectedPaymentMethodId;

    if (userSelectedPaymentMethodId) {
      return PaymentMethodsState.paymentMethods(paymentMethodsState).find(
        (pm) => pm.id === userSelectedPaymentMethodId
      )!;
    }

    return PaymentMethodsState.defaultPaymentMethod(paymentMethodsState);
  }

  @Selector()
  static insuranceUserData(state: OrderWizardDataStateModel): InsuranceUserData {
    return state.userData.insuranceStep;
  }

  @Selector()
  static productItemQuantitiesUserData(state: OrderWizardDataStateModel): ProductItemQuantityUserData[] | null {
    return state.userData.productsStep.quantities;
  }

  @Selector()
  static productItemDohUserData(state: OrderWizardDataStateModel): ProductItemDohUserData[] | null {
    return state.userData.productsStep.dohValues;
  }

  @Selector()
  static productItemOuDohUserData(state: OrderWizardDataStateModel): number | null {
    return state.userData.productsStep.ouDohValue;
  }

  @Selector()
  static productFrequenciesUserData(state: OrderWizardDataStateModel): ProductFrequenciesUserData | null {
    return state.userData.productsStep.frequencies;
  }

  @Selector()
  static productsUserData(state: OrderWizardDataStateModel): ProductsUserData {
    return state.userData.productsStep;
  }

  @Selector()
  static providerUserData(state: OrderWizardDataStateModel): ProviderUserData {
    return state.userData.providerStep;
  }

  @Selector()
  static selectedProvider(state: OrderWizardDataStateModel): SelectedProvider {
    let provider = state.providers.data.find(
      (provider) => provider.id === state.userData.providerStep.selectedProviderId
    );
    if (!provider) {
      const providerStepData = state.userData.providerStep;
      if (providerStepData.selectedProviderId === -1 && providerStepData.newProviderFormValues !== null) {
        provider = mapProviderFormValues(providerStepData.newProviderFormValues);
      }
    }
    return {
      provider: provider ? provider : null,
      lastProviderVisit: state.userData.providerStep.providerVisitFormValues.lastProviderVisit,
      nextProviderVisit: state.userData.providerStep.providerVisitFormValues.nextProviderVisit,
    };
  }

  @Selector()
  static contactsUserData(state: OrderWizardDataStateModel): ContactsUserData {
    return state.userData.contactsStep;
  }

  @Selector()
  static outOfPocket(state: OrderWizardDataStateModel): OutOfPocketMetStatus {
    const oop = state.copay.data;
    const outOfPocket: OutOfPocketMetStatus = {
      primaryOutOfPocketMet: oop?.primaryOutOfPocketMet,
      secondaryOutOfPocketMet: oop?.secondaryOutOfPocketMet,
    };

    return outOfPocket;
  }

  @Selector()
  static lastProviderVisitValidationState(state: OrderWizardDataStateModel): ApiStoreData<boolean | null> {
    return state.lastProviderVisitValidation;
  }

  @Selector()
  static nextProviderVisitValidationState(state: OrderWizardDataStateModel): ApiStoreData<boolean | null> {
    return state.nextProviderVisitValidation;
  }

  @Selector([LookupState, PatientInsurancesState, OrdersState])
  static isLoading(
    state: OrderWizardDataStateModel,
    lookupState: LookupStateModel,
    patientInsurancesState: PatientInsurancesStateModel,
    ordersState: OrdersStateModel
  ): boolean {
    return (
      // For order wizard
      // TODO add user state loading as a precaution
      // or add an initial global loading/is initiated state while waiting on some external dependencies
      lookupState.hcpcsGroups.isLoading ||
      lookupState.insuranceGroups.isLoading ||
      patientInsurancesState.insurance.isLoading ||
      state.reorder.isLoading ||
      ordersState.isLoading ||
      // For insurance step
      lookupState.relationshipsToInsured.isLoading ||
      lookupState.usStates.isLoading
    );
  }

  @Selector([LookupState, PatientInsurancesState, AddressesState, OrdersState])
  static errors(
    state: OrderWizardDataStateModel,
    lookupState: LookupStateModel,
    patientInsurancesState: PatientInsurancesStateModel,
    addressesState: AddressesStateModel,
    ordersState: OrdersStateModel
  ): CcsSentryError[] | null {
    const errors = [
      lookupState.hcpcsGroups.error,
      lookupState.insuranceGroups.error,
      patientInsurancesState.insurance.error,
      state.reorder.error,
      lookupState.relationshipsToInsured.error,
      lookupState.usStates.error,
      addressesState.addresses.error,

      // Patient info
      state.providers.error,

      // Reorder info
      ordersState.error,
    ].filter((e) => !!e);

    return errors.length === 0 ? null : (errors as CcsSentryError[]);
  }

  @Selector()
  static isProductsStepLoading(state: OrderWizardDataStateModel): boolean {
    return (
      // For products step
      state.datesOfService.isLoading || state.daysOnHandRequired.isLoading || state.frequenciesRequired.isLoading
    );
  }

  @Selector()
  static productsStepError(state: OrderWizardDataStateModel): CcsSentryError | null {
    // we gracefully fail regarding insurance requirements
    return null;
  }

  @Selector()
  static isProvidersStepLoading(state: OrderWizardDataStateModel): boolean {
    return (
      // For providers step
      state.providers.isLoading || state.providerVisitVerification.isLoading || state.providerVisits.isLoading
    );
  }

  @Selector()
  static providersStepError(state: OrderWizardDataStateModel): CcsSentryError | null {
    return state.providers.error;
  }

  @Selector([AddressesState, OrdersState])
  static isContactsStepLoading(
    state: OrderWizardDataStateModel,
    addressesState: AddressesStateModel,
    ordersState: OrdersStateModel
  ): boolean {
    return (
      // For contacts step
      ordersState.isLoading || addressesState.addresses.isLoading
    );
  }

  @Selector([AddressesState, OrdersState])
  static contactsStepErrors(
    state: OrderWizardDataStateModel,
    addressesState: AddressesStateModel,
    ordersState: OrdersStateModel
  ): CcsSentryError[] | null {
    const errors = [addressesState.addresses.error, ordersState.error].filter((e) => !!e);

    return errors.length === 0 ? null : (errors as CcsSentryError[]);
  }

  @Selector([PaymentMethodsState])
  static isBillingStepLoading(
    state: OrderWizardDataStateModel,
    paymentMethodsState: PaymentMethodsStateModel
  ): boolean {
    return (
      // For billing step
      state.copay.isLoading || state.copay.isUpdating || paymentMethodsState.paymentMethods.isLoading
    );
  }

  @Selector()
  static billingStepError(state: OrderWizardDataStateModel): CcsSentryError | null {
    // we gracefully fail regarding copay/payment methods errors
    return null;
  }

  @Selector([PaymentMethodsState])
  static isSummaryStepLoading(
    state: OrderWizardDataStateModel,
    paymentMethodsState: PaymentMethodsStateModel
  ): boolean {
    return (
      // For Summary step
      state.copay.isLoading || state.copay.isUpdating || paymentMethodsState.paymentMethods.isLoading
    );
  }

  @Selector()
  static summaryStepError(state: OrderWizardDataStateModel): CcsSentryError | null {
    // we gracefully fail regarding copay/payment methods errors
    return null;
  }

  @Selector()
  static allowDeductibleOverrides(state: OrderWizardDataStateModel): boolean {
    const showSummaryStepBillingInfo = !this.hideSummaryStepBillingInfo(state).value;
    const copayData = state.copay.data;
    const excludeCopay = state.copay.data?.excludeCopay;
    const hasCopayDeductibleOverrides =
      !!copayData?.allowOverridingPrimaryDeductible || !!copayData?.allowOverridingSecondaryDeductible;
    const hasNonZeroResponsibility = !!copayData?.estimatedPatientResponsibility;
    const hasAppliedDeductibleOverrides = this.hasAppliedDeductibleOverrides(state);

    return (
      showSummaryStepBillingInfo &&
      !excludeCopay &&
      hasCopayDeductibleOverrides &&
      (hasNonZeroResponsibility || hasAppliedDeductibleOverrides)
    );
  }

  @Selector()
  static deductibleOverridesUserData(state: OrderWizardDataStateModel): DeductibleOverridesFormValues | null {
    return state.userData.billingStep.deductibleOverrides;
  }

  @Selector()
  static appliedDeductibleOverridesUserData(state: OrderWizardDataStateModel): DeductibleOverridesFormValues | null {
    return state.userData.billingStep.appliedDeductibleOverrides;
  }

  @Selector()
  static hasAppliedDeductibleOverrides(state: OrderWizardDataStateModel): boolean {
    return (
      state.userData.billingStep.appliedDeductibleOverrides !== null &&
      !isEqual(state.userData.billingStep.appliedDeductibleOverrides, {
        overridePrimaryDeductible: false,
        overrideSecondaryDeductible: false,
      })
    );
  }

  @Selector()
  static hasUnsubmittedDeductibleOverrides(state: OrderWizardDataStateModel): boolean {
    const doUserData = state.userData.billingStep.deductibleOverrides;
    const doSubmitted = state.userData.billingStep.submittedDeductibleOverrides;

    return !isEqual(
      doUserData || { overridePrimaryDeductible: false, overrideSecondaryDeductible: false },
      doSubmitted || { overridePrimaryDeductible: false, overrideSecondaryDeductible: false }
    );
  }

  @Selector()
  static isShipmentDelayed(state: OrderWizardDataStateModel): boolean {
    const computedTargetShipDate = this.computedTargetShipDate(state);
    const orderTargetShipDate = state.reorder.data?.targetedShipDate;

    return !!computedTargetShipDate && !!orderTargetShipDate
      ? moment(computedTargetShipDate).startOf('day').isAfter(moment(orderTargetShipDate).startOf('day'))
      : false;
  }

  @Selector()
  static canAutoComputeTargetShipDate(state: OrderWizardDataStateModel): OrderWizardStatus {
    const hasUnknownOrInvalidDosData =
      state.datesOfService.isLoading || state.datesOfService.isUpdating || !!state.datesOfService.error;
    const dosData = state.datesOfService.data;
    const dosHasMissingReorderItems = dosData?.hasMissingItems;
    const hasUnknownShipOrConsentDates = dosData?.hasUnknownItemDates;
    const hasDifferentItemShipDates = dosData?.hasDifferentItemShipDates;
    const hasFutureItemConsentDates = dosData?.hasFutureItemConsentDates;
    const hasUnknownDosEarliestOrderCanShip = !dosData?.earliestOrderCanShip;

    const canAutoComputeTargetShipDate =
      !dosHasMissingReorderItems &&
      !hasUnknownOrInvalidDosData &&
      !hasUnknownShipOrConsentDates &&
      !hasDifferentItemShipDates &&
      !hasFutureItemConsentDates &&
      !hasUnknownDosEarliestOrderCanShip;

    return {
      value: canAutoComputeTargetShipDate,
      debugInfo: {
        dosHasMissingReorderItems,
        hasUnknownOrInvalidDosData,
        hasUnknownShipOrConsentDates,
        hasDifferentItemShipDates,
        hasFutureItemConsentDates,
        hasUnknownDosEarliestOrderCanShip,
      },
    };
  }

  @Selector()
  static isInsuranceChanged(state: OrderWizardDataStateModel): boolean {
    return !!state.userData.insuranceStep.primaryInsurance;
  }

  @Selector()
  static skipCollectionForReconsent(state: OrderWizardDataStateModel): boolean {
    return !(
      !state.collectionStatus.data ||
      state.collectionStatus.data.status === CollectionStatus.Unknown ||
      state.collectionStatus.data.status === CollectionStatus.CollectionNeeded
    );
  }

  @Selector()
  static doNotFetchCopay(state: OrderWizardDataStateModel): OrderWizardStatus {
    const hasUnknownOrInvalidReorderData = hasUnknownOrInvalidData(state.reorder);

    const reorderData = state.reorder.data;
    const isNonBillableShipmentType = reorderData?.isNonBillableShipmentType;
    const isInsuranceChanged = this.isInsuranceChanged(state);
    const canAutoComputeTargetShipDate = this.canAutoComputeTargetShipDate(state).value;
    const skipCollectionForReconsent = OrderWizardDataState.skipCollectionForReconsent(state);

    const doNotFetchCopay =
      (!canAutoComputeTargetShipDate && !window.location.pathname.includes('/make-payment')) ||
      hasUnknownOrInvalidReorderData ||
      isNonBillableShipmentType ||
      isInsuranceChanged ||
      skipCollectionForReconsent;

    return {
      value: doNotFetchCopay,
      debugInfo: {
        canAutoComputeTargetShipDate,
        hasUnknownOrInvalidReorderData,
        isNonBillableShipmentType,
        isInsuranceChanged,
        skipCollectionForReconsent,
      },
    };
  }

  @Selector()
  static hideSummaryStepBillingInfo(state: OrderWizardDataStateModel): OrderWizardStatus {
    const doNotFetchCopay = this.doNotFetchCopay(state).value;
    const hasUnknownOrInvalidCopayData = state.copay.isLoading || state.copay.isUpdating || !!state.copay.error;

    const hideSummaryStepBillingInfo = doNotFetchCopay || hasUnknownOrInvalidCopayData;

    return {
      value: hideSummaryStepBillingInfo,
      debugInfo: {
        doNotFetchCopay,
        hasUnknownOrInvalidCopayData,
      },
    };
  }
  // HSK
  @Selector()
  static requireOrderConfirmation(state: OrderWizardDataStateModel): boolean {
    return !this.hideSummaryStepBillingInfo(state).value || this.skipCollectionForReconsent(state);
  }

  @Selector([PaymentMethodsState])
  static hasUnknownOrInvalidPaymentMethods(
    state: OrderWizardDataStateModel,
    paymentMethodsState: PaymentMethodsStateModel
  ): boolean {
    return hasUnknownOrInvalidData(paymentMethodsState.paymentMethods);
  }

  @Selector([PaymentMethodsState])
  static skipBillingStep(
    state: OrderWizardDataStateModel,
    paymentMethodsState: PaymentMethodsStateModel
  ): OrderWizardStatus {
    const copayData = state.copay.data;

    const patientResponsiblityLessThanOrEqualToThreshold =
      copayData?.estimatedPatientResponsibility || copayData?.estimatedPatientResponsibility === 0
        ? copayData?.estimatedPatientResponsibility <= COLLECT_PAYMENT_THRESHOLD_USD
        : false;

    const hideSummaryStepBillingInfo = this.hideSummaryStepBillingInfo(state).value;
    const hasUnknownOrInvalidPaymentMethods = this.hasUnknownOrInvalidPaymentMethods(state, paymentMethodsState);
    const allowDeductibleOverrides = this.allowDeductibleOverrides(state);
    const hasAppliedDeductibleOverrides = this.hasAppliedDeductibleOverrides(state);

    const skipBillingStep =
      hideSummaryStepBillingInfo ||
      (patientResponsiblityLessThanOrEqualToThreshold &&
        (!allowDeductibleOverrides || !hasAppliedDeductibleOverrides)) ||
      hasUnknownOrInvalidPaymentMethods;

    return {
      value: skipBillingStep,
      debugInfo: {
        hideSummaryStepBillingInfo,
        patientResponsiblityLessThanOrEqualToThreshold,
        allowDeductibleOverrides,
        hasAppliedDeductibleOverrides,
        hasUnknownOrInvalidPaymentMethods,
      },
    };
  }

  @Selector([PaymentMethodsState])
  static paymentOptional(state: OrderWizardDataStateModel, paymentMethodsState: PaymentMethodsStateModel): boolean {
    const copayData = state.copay.data;
    const estimatedPatientResponsibility = copayData?.estimatedPatientResponsibility;
    const skipBillingStep = this.skipBillingStep(state, paymentMethodsState).value;

    return !skipBillingStep && (estimatedPatientResponsibility || estimatedPatientResponsibility === 0)
      ? estimatedPatientResponsibility <= COLLECT_PAYMENT_OPTIONAL_THRESHOLD_USD
      : false;
  }

  @Selector()
  static exitOrderWizard(state: OrderWizardDataStateModel): ExitOrderWizardStatus {
    let exitOrderWizard = false;
    let isUpdating = state.datesOfService.isUpdating;
    let messageKey = 'default';

    const hasUnknownDosResult = !state.datesOfService.data?.dosResult;
    const ProviderVisitUserData = state.userData.providerStep.providerVisitFormValues;
    const lpvValid = state.lastProviderVisitValidation.data;
    const npvValid = state.nextProviderVisitValidation.data;

    const cannotConsentOrderDueToDaysOnHand = state.datesOfService.data?.dosResult === DosResult.End;
    const invalidLastVisitAndMissingNextVisit = lpvValid === false && !ProviderVisitUserData.nextProviderVisit;
    const invalidLastVisitAndInvalidNextVisit = lpvValid === false && npvValid === false;

    exitOrderWizard =
      (!hasUnknownDosResult && cannotConsentOrderDueToDaysOnHand) ||
      invalidLastVisitAndMissingNextVisit ||
      invalidLastVisitAndInvalidNextVisit;

    if (cannotConsentOrderDueToDaysOnHand) {
      messageKey = 'cannotConsentOrder';
    } else if (invalidLastVisitAndMissingNextVisit) {
      messageKey = 'missingNextProviderVisit';
    } else if (invalidLastVisitAndInvalidNextVisit) {
      messageKey = 'invalidNextProviderVisit';
    }

    return {
      value: exitOrderWizard,
      debugInfo: {
        hasUnknownDosResult,
        cannotConsentOrderDueToDaysOnHand,
        invalidLastVisitAndMissingNextVisit,
        invalidLastVisitAndInvalidNextVisit,
      },
      isUpdating,
      messageKey,
    };
  }

  @Selector()
  static initialTargetShipDate(state: OrderWizardDataStateModel): string | null {
    let initialTargetShipDate = state.reorder.data?.targetedShipDate || null;

    if (this.canAutoComputeTargetShipDate(state).value)
      initialTargetShipDate = state.datesOfService.data!.earliestOrderCanShip!;

    return initialTargetShipDate;
  }

  @Selector()
  static computedTargetShipDate(state: OrderWizardDataStateModel): string | null {
    let computedTargetShipDate = OrderWizardDataState.initialTargetShipDate(state);

    if (this.canAutoComputeTargetShipDate(state).value) {
      const providerVisitValues = state.userData.providerStep.providerVisitFormValues;

      const nextProviderVisitDate = moment(providerVisitValues.nextProviderVisit).startOf('day');
      const npvValid = state.nextProviderVisitValidation.data;

      if (
        providerVisitValues.nextProviderVisit &&
        npvValid === true &&
        nextProviderVisitDate.isAfter(moment(computedTargetShipDate).startOf('day'))
      ) {
        computedTargetShipDate =
          nextProviderVisitDate.day() === 0
            ? nextProviderVisitDate.add(1, 'days').format('YYYY-MM-DD')
            : nextProviderVisitDate.format('YYYY-MM-DD');
      }
    }

    return computedTargetShipDate;
  }

  @Selector()
  static isMissingPwoDocs(state: OrderWizardDataStateModel): boolean {
    const areItemsMissingDocs = state.reorder.data?.items.some((i) => i.isMissingDoc);

    const addedNewProvider = !!state.userData.providerStep.newProviderFormValues;

    return areItemsMissingDocs || addedNewProvider;
  }

  @Selector()
  static isShippingSoon(state: OrderWizardDataStateModel): boolean {
    const computedTargetShipDate = OrderWizardDataState.computedTargetShipDate(state);

    return !!computedTargetShipDate
      ? moment(computedTargetShipDate)
          .startOf('day')
          .isSameOrBefore(moment().add(SHIPPING_SOON_THRESHOLD_DAYS, 'days').startOf('day'))
      : false;
  }

  @Selector()
  static isOrderConfirmed(state: OrderWizardDataStateModel): boolean {
    return state.userData.isOrderConfirmed;
  }

  fetchReorderDetails(ctx: StateContext<OrderWizardDataStateModel>, patientId: number, reorderId: number) {
    ctx.setState(
      produce((draft) => {
        draft.reorder.isLoading = true;
      })
    );

    let missingDataErrorMessage = null;

    const patientOrders = this.store.selectSnapshot(OrdersState.allOrders);
    const foundPatientOrder = patientOrders.find((o) => o.id === reorderId);
    const correspondingShipment = patientOrders.find((o) => o.originatingReorderId === reorderId);

    const isMedicareOrMedicareRepl = this.store.selectSnapshot(
      (state) => state.patientInsurances.insurance.data?.isMedicareOrMedicareRepl
    );

    const hcpcsGroups = this.store.selectSnapshot(LookupState.hcpcsGroups);

    if (!patientOrders) {
      missingDataErrorMessage = 'Missing patient orders';
    } else if (!foundPatientOrder) {
      if (correspondingShipment) {
        missingDataErrorMessage = `This order is already processed. Please follow progress for shipment number ${correspondingShipment.id}`;
      } else {
        missingDataErrorMessage = `Order ${reorderId} not found`;
      }
    } else if (
      !foundPatientOrder.needsAuthorization &&
      foundPatientOrder.status !== OneDotOrderStatus.PaymentRequired
    ) {
      missingDataErrorMessage = `This order does not need authorization at this time. Current status: ${foundPatientOrder.status}`;
    } else if (isMedicareOrMedicareRepl === undefined) {
      missingDataErrorMessage = 'Missing medicare status';
    } else if (!hcpcsGroups.length) {
      missingDataErrorMessage = 'Missing HCPCs groups';
    }

    if (missingDataErrorMessage) {
      return this.handleError({
        ctx,
        error: new MissingApiRequestDataError([missingDataErrorMessage]),
        stateKey: 'reorder',
      });
    }

    return (
      foundPatientOrder!.isShipment
        ? this.apiService.getShipmentDetails(patientId, reorderId)
        : this.apiService.getReorderDetails(patientId, reorderId)
    ).pipe(
      tap({
        next: (response) => {
          const data = mapReorderDetails(response.data[0], isMedicareOrMedicareRepl!, hcpcsGroups);
          const duplicateItems = data.items.filter((i) => i.isDuplicate);
          const nonDuplicateItems = data.items.filter((i) => !i.isDuplicate);
          const hasInvalidDuplicateItems =
            uniqBy(duplicateItems, 'id').length !== duplicateItems.length ||
            uniqBy(nonDuplicateItems, 'id').length !== nonDuplicateItems.length;

          if (data.items.length === 0) {
            const error = new InvalidApiResponseDataError(['Missing reorder items']);
            this.handleError({ ctx, error, stateKey: 'reorder' });
          } else if (hasInvalidDuplicateItems) {
            const error = new InvalidApiResponseDataError(['Reorder has invalid duplicate items']);
            this.handleError({ ctx, error, stateKey: 'reorder' });
          } else if (!data.targetedShipDate) {
            const error = new InvalidApiResponseDataError(['Missing reorder target ship date']);
            this.handleError({ ctx, error, stateKey: 'reorder' });
          } else if (!data.shipmentTypeId) {
            const error = new InvalidApiResponseDataError(['Missing reorder shipment type id']);
            this.handleError({ ctx, error, stateKey: 'reorder' });
          } else {
            this.handleSuccess({ ctx, data, stateKey: 'reorder' });
          }
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'reorder' }))
    );
  }

  fetchDatesOfService(
    ctx: StateContext<OrderWizardDataStateModel>,
    patientId: number,
    dohUserData?: ProductItemDohUserData[]
  ) {
    const state = ctx.getState();
    const reorder = state.reorder.data;
    const reorderItems = reorder?.items;
    const daysOnHandRequired = state.daysOnHandRequired.data;

    let errors: string[] = [];

    if (!reorder) {
      errors = [...errors, 'Missing reorder'];
    }

    if (!reorder?.shipmentTypeId) {
      errors = [...errors, 'Missing shipment type id'];
    }

    if (!reorderItems || reorderItems.length === 0) {
      errors = [...errors, 'Missing reorder items'];
    }

    const isUpdatingDos = !!dohUserData;
    const loadingStateKey = isUpdatingDos ? 'isUpdating' : 'isLoading';
    const errorStateKey = isUpdatingDos ? 'errorUpdating' : 'error';

    if (errors.length > 0) {
      const error = new MissingApiRequestDataError(errors);
      return this.handleError({ ctx, error, stateKey: 'datesOfService', errorStateKey, updating: isUpdatingDos });
    }

    const requestId = uniqueId();

    ctx.setState(
      produce((draft) => {
        draft.datesOfService[loadingStateKey] = true;
        draft.datesOfService.requestId = requestId;
      })
    );

    // Needed to fix race condition bug. If this is an updating request, with isLoading still true,
    // isLoading will never be reset on its own
    // TODO support in handleSuccess/Error functions or with store swapping to elf
    const updateStateFunction = (draft: OrderWizardDataStateModel) => {
      draft.datesOfService.isLoading = false; // in all cases
    };

    return this.apiService
      .getDatesOfService(buildDatesOfServiceRequest(patientId, reorder!, daysOnHandRequired, dohUserData))
      .pipe(
        tap({
          next: (response) => {
            if (requestId !== ctx.getState().datesOfService.requestId) return;
            const datesOfService = mapDatesOfService(response.data, reorder!);

            if (!datesOfService.dosResult) {
              const error = new InvalidApiResponseDataError(['Unknown dates of service result']);
              this.handleError({
                ctx,
                error,
                stateKey: 'datesOfService',
                errorStateKey,
                updateStateFunction,
                updating: isUpdatingDos,
              });
            } else if (!isUpdatingDos && datesOfService.dosResult === DosResult.End) {
              const error = new InvalidApiResponseDataError([
                'Dates of service result is "End" on loading the reorder',
              ]);
              this.handleError({
                ctx,
                error,
                stateKey: 'datesOfService',
                errorStateKey,
                updateStateFunction,
                updating: isUpdatingDos,
              });
            } else {
              const data = datesOfService;
              this.handleSuccess({
                ctx,
                data,
                stateKey: 'datesOfService',
                errorStateKey,
                updateStateFunction,
                updating: isUpdatingDos,
              });
            }
          },
        }),
        catchError(
          (error: Error): Observable<any> =>
            requestId === ctx.getState().datesOfService.requestId
              ? this.handleError({
                  ctx,
                  error,
                  stateKey: 'datesOfService',
                  errorStateKey,
                  updateStateFunction,
                  updating: isUpdatingDos,
                })
              : of(null)
        )
      );
  }

  fetchDaysOnHandRequired(ctx: StateContext<OrderWizardDataStateModel>, patientId: number) {
    const itemIds = this.getItemIds(ctx);
    const state = ctx.getState();
    const reorder = state.reorder.data;

    let errors: string[] = [];

    if (!reorder) {
      errors = [...errors, 'Missing reorder'];
    }

    if (!reorder?.targetedShipDate) {
      errors = [...errors, 'Missing reorder target ship date'];
    }

    if (!itemIds || itemIds.length === 0) {
      errors = [...errors, 'Missing reorder items'];
    }

    if (errors.length > 0) {
      const error = new MissingApiRequestDataError(errors);
      return this.handleError({ ctx, error, stateKey: 'daysOnHandRequired' });
    }

    ctx.setState(
      produce((draft) => {
        draft.daysOnHandRequired.isLoading = true;
      })
    );

    return this.apiService
      .getDaysOnHandRequired(patientId, {
        patientId,
        supplies: itemIds.map((id) => ({ itemId: id })) || [],
      })
      .pipe(
        tap({
          next: (response) => {
            const data = response.data.map((doh) => mapDaysOnHandRequired(doh, reorder!));
            this.handleSuccess({ ctx, data, stateKey: 'daysOnHandRequired' });
          },
        }),
        catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'daysOnHandRequired' }))
      );
  }

  fetchFrequenciesRequired(ctx: StateContext<OrderWizardDataStateModel>, patientId: number) {
    const itemIds = this.getItemIds(ctx);
    const dateOfService = ctx.getState().reorder.data?.targetedShipDate;

    ctx.setState(
      produce((draft) => {
        draft.frequenciesRequired.isLoading = true;
      })
    );

    return this.apiService
      .getFrequenciesRequired(patientId, {
        patientId,
        dateOfService,
        supplies: itemIds.map((id) => ({ itemId: id })) || [],
      })
      .pipe(
        tap({
          next: (response) => {
            const data = mapFrequenciesRequired(response.data[0]);
            this.handleSuccess({ ctx, data, stateKey: 'frequenciesRequired' });
          },
        }),
        catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'frequenciesRequired' }))
      );
  }

  fetchProviders(ctx: StateContext<OrderWizardDataStateModel>, patientId: number) {
    const shipmentTypeId = ctx.getState().reorder.data?.shipmentTypeId;

    if (!shipmentTypeId) {
      const error = new MissingApiRequestDataError(['Missing shipment type Id']);
      return this.handleError({ ctx, error, stateKey: 'providers' });
    }

    ctx.setState(
      produce((draft) => {
        draft.providers.isLoading = true;
      })
    );

    return this.apiService.getProviders(patientId, shipmentTypeId).pipe(
      tap({
        next: (response) => {
          const data = response.data.map((res) => mapProvider(res));
          this.handleSuccess({ ctx, data, stateKey: 'providers' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'providers' }))
    );
  }

  fetchProviderVisitVerification(ctx: StateContext<OrderWizardDataStateModel>, patientId: number) {
    const itemIds = this.getItemIds(ctx);

    ctx.setState(
      produce((draft) => {
        draft.providerVisitVerification.isLoading = true;
      })
    );

    const requestBody = { patientId, supplies: itemIds.map((id) => ({ itemId: id })) };

    return this.apiService.getProviderVisitVerification(patientId, requestBody).pipe(
      tap({
        next: (response) => {
          const data = mapProviderVisitVerification(response.data[0]);
          this.handleSuccess({ ctx, data, stateKey: 'providerVisitVerification' });
        },
      }),
      catchError(
        (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'providerVisitVerification' })
      )
    );
  }

  fetchProviderVisits(ctx: StateContext<OrderWizardDataStateModel>, patientId: number) {
    const shipmentTypeId = ctx.getState().reorder.data?.shipmentTypeId;
    const dateOfService = ctx.getState().reorder.data?.targetedShipDate;
    const doctorIds = this.getDoctorIds(ctx);
    const itemIds = this.getItemIds(ctx);

    if (!shipmentTypeId || !dateOfService) {
      let errors: string[] = [];

      if (!shipmentTypeId) {
        errors = [...errors, 'Missing shipment type Id'];
      }

      if (!dateOfService) {
        errors = [...errors, 'Missing date of service'];
      }

      const error = new MissingApiRequestDataError(errors);
      return this.handleError({ ctx, error, stateKey: 'providerVisits' });
    }

    ctx.setState(
      produce((draft) => {
        draft.providerVisits.isLoading = true;
      })
    );

    const requestBody: ApiProviderVisitsRequest = {
      patientId,
      shipmentType: shipmentTypeId!,
      dateOfService: dateOfService!,
      doctors: doctorIds.map((id) => ({ doctorId: id })),
      supplies: itemIds.map((id) => ({ itemId: id })),
    };

    return this.apiService.getProviderVisits(patientId, requestBody).pipe(
      tap({
        next: (response) => {
          const data = response.data.map((res) => mapProviderVisit(res));
          this.handleSuccess({ ctx, data, stateKey: 'providerVisits' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'providerVisits' }))
    );
  }

  fetchCollectionStatus(ctx: StateContext<OrderWizardDataStateModel>, patientId: number) {
    const reorder = ctx.getState().reorder.data;
    const patientOrders = this.store.selectSnapshot(OrdersState.allOrders);
    const foundPatientOrder = patientOrders.find((o) => o.id === reorder?.id);

    let error = null;

    if (!reorder) {
      error = new MissingApiRequestDataError(['Missing reorder']);
    } else if (!foundPatientOrder) {
      error = new MissingApiRequestDataError(['Reorder not found in patient orders']);
    }

    if (error) {
      return this.handleError({ ctx, error, stateKey: 'collectionStatus' });
    }

    if (!foundPatientOrder!.needsReconsent) {
      return of(null);
    }

    ctx.setState(
      produce((draft) => {
        draft.collectionStatus.isLoading = true;
      })
    );

    return this.apiService.getShipmentCollectionStatus(patientId, reorder!.id).pipe(
      tap({
        next: (response) => {
          const data = mapCollectionStatus(response.data);
          this.handleSuccess({ ctx, data, stateKey: 'collectionStatus' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'collectionStatus' }))
    );
  }

  fetchCopay(
    ctx: StateContext<OrderWizardDataStateModel>,
    patientId: number,
    options: FetchCopayOptions = {
      isUpdating: false,
      isUpdatingDeductibleOverrides: false,
    }
  ) {
    const state = ctx.getState();

    if (OrderWizardDataState.doNotFetchCopay(state).value) return of(null);

    const reorder = state.reorder.data;
    const reorderItems = reorder?.items;
    const datesOfService = state.datesOfService.data;
    const computedTargetShipDate = OrderWizardDataState.computedTargetShipDate(state);

    let errors: string[] = [];

    if (!reorder) {
      errors = [...errors, 'Missing reorder'];
    }

    if (!computedTargetShipDate) {
      errors = [...errors, 'Missing computed target ship date'];
    }

    if (!reorderItems) {
      errors = [...errors, 'Missing reorder items'];
    }

    if (reorderItems?.some((i) => !i.shipmentTypeId)) {
      errors = [...errors, 'Missing shipment type on one or more items'];
    }

    if (reorderItems?.some((i) => i.quantity === null || i.quantity === undefined)) {
      errors = [...errors, 'Missing quantity on one or more items'];
    }

    if (!datesOfService) {
      errors = [...errors, 'Missing dates of service data'];
    }

    if (errors.length > 0) {
      const error = new MissingApiRequestDataError(errors);
      return this.handleError({ ctx, error, stateKey: 'copay' });
    }

    const enableDeductibleOverrides = this.envService.features['deductibleOverrides']?.enabled;
    const enableCopayExclusion = this.envService.features['copayExclusion']?.enabled;
    const useExclusionList = enableCopayExclusion;
    const deductibleOverrides = state.userData.billingStep.deductibleOverrides;

    const requestBody = buildCopayRequest(
      patientId,
      reorderItems!,
      datesOfService!,
      computedTargetShipDate!,
      state.userData.productsStep,
      useExclusionList,
      deductibleOverrides
    );

    const requestId = uniqueId();

    if (options.isUpdatingDeductibleOverrides) {
      const stateKey = 'updateCopayDeductibleOverrides';

      ctx.setState(
        produce((draft) => {
          draft[stateKey].isLoading = true;
          draft[stateKey].requestId = requestId;
        })
      );

      const currentSubmittedDeductibleOverrides = state.userData.billingStep.submittedDeductibleOverrides;

      ctx.setState(
        produce((draft) => {
          draft.userData.billingStep.submittedDeductibleOverrides = deductibleOverrides;
        })
      );

      return this.apiService.getCopay(requestBody).pipe(
        tap({
          next: (response) => {
            if (requestId !== ctx.getState()[stateKey].requestId) return;
            const data = mapCopay(response.data, enableCopayExclusion, enableDeductibleOverrides);

            const updateStateFunction = (draft: OrderWizardDataStateModel) => {
              draft.copay.data = data;
              draft.userData.billingStep.appliedDeductibleOverrides = deductibleOverrides;
            };

            this.handleSuccess({ ctx, stateKey, updateStateFunction });
          },
        }),
        catchError((error: Error): Observable<any> => {
          if (requestId !== ctx.getState()[stateKey].requestId) return of(null);

          const updateStateFunction = (draft: OrderWizardDataStateModel) => {
            draft.userData.billingStep.submittedDeductibleOverrides = currentSubmittedDeductibleOverrides;
          };

          return this.handleError({ ctx, error, stateKey, updateStateFunction });
        })
      );
    }

    const stateKey = 'copay';
    const loadingStateKey = options.isUpdating ? 'isUpdating' : 'isLoading';

    ctx.setState(
      produce((draft) => {
        draft[stateKey][loadingStateKey] = true;
        draft[stateKey].requestId = requestId;
      })
    );

    // Needed to fix race condition bug. If this is an updating request, with isLoading still true,
    // isLoading will never be reset on its own
    // TODO support in handleSuccess/Error functions or with store swapping to elf
    const updateStateFunction = (draft: OrderWizardDataStateModel) => {
      draft[stateKey].isLoading = false; // in all cases
    };

    return this.apiService.getCopay(requestBody).pipe(
      tap({
        next: (response) => {
          if (requestId !== ctx.getState()[stateKey].requestId) return;
          const data = mapCopay(response.data, enableCopayExclusion, enableDeductibleOverrides);
          this.handleSuccess({ ctx, data, stateKey, updateStateFunction, updating: options.isUpdating });
        },
      }),
      catchError(
        (error: Error): Observable<any> =>
          requestId === ctx.getState()[stateKey].requestId
            ? this.handleError({ ctx, error, stateKey, updateStateFunction, updating: options.isUpdating })
            : of(null)
      )
    );
  }

  refetchCopayIfNeeded(
    ctx: StateContext<OrderWizardDataStateModel>,
    patientId: number,
    previousTargetShipDate: string | null,
    quantitiesUpdated: boolean = false
  ) {
    const state = ctx.getState();

    if (OrderWizardDataState.doNotFetchCopay(state).value) return of(null);

    const currentTargetShipDate = OrderWizardDataState.computedTargetShipDate(state);

    if (
      (previousTargetShipDate &&
        currentTargetShipDate &&
        !moment(previousTargetShipDate).startOf('day').isSame(moment(currentTargetShipDate).startOf('day'))) ||
      quantitiesUpdated
    ) {
      return this.fetchCopay(ctx, patientId, { isUpdating: true });
    }

    return of(null);
  }

  @Action(UpdateCopayWithDeductibleOverrides)
  updateCopayWithDeductibleOverrides(ctx: StateContext<OrderWizardDataStateModel>) {
    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: null });
    }

    return this.fetchCopay(ctx, patientId, { isUpdatingDeductibleOverrides: true });
  }

  @Action(RefetchDosAndCopayIfNeeded)
  refetchDosAndCopayIfNeededActionHandler(
    ctx: StateContext<OrderWizardDataStateModel>,
    action: RefetchDosAndCopayIfNeeded
  ) {
    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const previousTargetShipDate = OrderWizardDataState.computedTargetShipDate(ctx.getState());
    const dohValues = ctx.getState().userData.productsStep.dohValues;

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: null });
    }

    return concat(
      this.fetchDatesOfService(ctx, patientId, dohValues || undefined),
      defer(this.refetchCopayIfNeeded.bind(this, ctx, patientId, previousTargetShipDate, action.haveQuantitiesChanged))
    );
  }

  @Action(RefetchCopayIfNeeded)
  refetchCopayIfNeededActionHandler(ctx: StateContext<OrderWizardDataStateModel>, action: RefetchCopayIfNeeded) {
    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: null });
    }

    return this.refetchCopayIfNeeded(ctx, patientId, action.previousTargetShipDate, action.quantitiesUpdated);
  }

  getItemIds(ctx: StateContext<OrderWizardDataStateModel>): number[] {
    const state = ctx.getState();
    return state.reorder.data ? uniq((state.reorder.data.items || []).map((roi: ReorderDetailsItem) => roi.id)) : [];
  }

  getDoctorIds(ctx: StateContext<OrderWizardDataStateModel>): number[] {
    const state = ctx.getState();
    return state.providers.data ? (state.providers.data || []).map((p: Provider) => p.id) : [];
  }

  @Action(LoadOrderWizard)
  loadOrderWizard(ctx: StateContext<OrderWizardDataStateModel>, action: LoadOrderWizard) {
    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: null });
    }

    return merge(
      // Fire long call chains first
      concat(
        this.store.dispatch(new FetchOrdersAndReorders()),
        defer(this.fetchReorderDetails.bind(this, ctx, patientId, action.reorderId)),
        iif(
          () => !ctx.getState().reorder.error,
          merge(
            defer(this.fetchFrequenciesRequired.bind(this, ctx, patientId)),
            concat(
              defer(this.fetchDaysOnHandRequired.bind(this, ctx, patientId)),
              defer(this.fetchDatesOfService.bind(this, ctx, patientId)),
              defer(this.fetchCollectionStatus.bind(this, ctx, patientId)),
              defer(this.fetchCopay.bind(this, ctx, patientId))
            ),
            concat(
              merge(
                defer(this.fetchProviderVisitVerification.bind(this, ctx, patientId)),
                defer(this.fetchProviders.bind(this, ctx, patientId))
              ),
              defer(this.fetchProviderVisits.bind(this, ctx, patientId))
            )
          ),
          of(null)
        )
      ),
      this.store.dispatch(new FetchPatientPaymentMethods())
    );
  }

  @Action(SetInsuranceUserData)
  setInsuranceUserData(ctx: StateContext<OrderWizardDataStateModel>, action: SetInsuranceUserData) {
    ctx.setState(
      produce((draft) => {
        draft.userData.insuranceStep[action.insuranceType] = action.insuranceData;
      })
    );
  }

  @Action(SetProductItemQuantitiesUserData)
  setProductItemQuantitiesUserData(
    ctx: StateContext<OrderWizardDataStateModel>,
    action: SetProductItemQuantitiesUserData
  ) {
    ctx.setState(
      produce((draft) => {
        draft.userData.productsStep.quantities = action.quantities;
      })
    );
  }

  @Action(SetProductItemsDohUserData)
  setProductItemsDohUserData(ctx: StateContext<OrderWizardDataStateModel>, action: SetProductItemsDohUserData) {
    ctx.setState(
      produce((draft) => {
        draft.userData.productsStep.dohValues = action.dohValues;
      })
    );
  }

  @Action(SetProductItemsOuDohUserData)
  setProductItemsOuDohUserData(ctx: StateContext<OrderWizardDataStateModel>, action: SetProductItemsOuDohUserData) {
    ctx.setState(
      produce((draft) => {
        draft.userData.productsStep.ouDohValue = action.doh;
      })
    );
  }

  @Action(SetProductFrequenciesUserData)
  setProductFrequenciesUserData(ctx: StateContext<OrderWizardDataStateModel>, action: SetProductFrequenciesUserData) {
    ctx.setState(
      produce((draft) => {
        draft.userData.productsStep.frequencies = action.frequencies;
      })
    );
  }

  @Action(SetProviderUserData)
  setProviderStepForm(ctx: StateContext<OrderWizardDataStateModel>, action: SetProviderUserData) {
    const previousTargetShipDate = OrderWizardDataState.computedTargetShipDate(ctx.getState());
    const previousLpv = ctx.getState().userData.providerStep.providerVisitFormValues.lastProviderVisit;
    const previousNpv = ctx.getState().userData.providerStep.providerVisitFormValues.nextProviderVisit;
    const lpv = action.providerStepFormValues.providerVisitFormValues.lastProviderVisit;
    const npv = action.providerStepFormValues.providerVisitFormValues.nextProviderVisit;

    ctx.setState(
      produce((draft) => {
        draft.userData.providerStep.selectedProviderId = action.providerStepFormValues.selectedProviderId;
        draft.userData.providerStep.providerVisitFormValues = action.providerStepFormValues.providerVisitFormValues;
      })
    );

    if (lpv && !moment(lpv).isSame(moment(previousLpv), 'days')) {
      this.store.dispatch(
        new ValidateProviderVisit(moment(lpv).startOf('day').format('YYYY-MM-DD'), true, previousTargetShipDate)
      );
    } else if (npv && !moment(npv).isSame(moment(previousNpv), 'days')) {
      this.store.dispatch(
        new ValidateProviderVisit(moment(npv).startOf('day').format('YYYY-MM-DD'), false, previousTargetShipDate)
      );
    }
  }

  @Action(SetNewProviderUserData)
  setNewProviderForm(ctx: StateContext<OrderWizardDataStateModel>, action: SetNewProviderUserData) {
    ctx.setState(
      produce((draft) => {
        draft.userData.providerStep.newProviderFormValues = action.newProviderFormValues;
      })
    );
  }

  @Action(SetContactsUserData)
  setContactsUserData(ctx: StateContext<OrderWizardDataStateModel>, action: SetContactsUserData) {
    ctx.setState(
      produce((draft) => {
        draft.userData.contactsStep = action.userData;
      })
    );

    return of(null);
  }

  @Action(SetSelectedPaymentMethodUserData)
  setSelectedPaymentMethodUserData(
    ctx: StateContext<OrderWizardDataStateModel>,
    action: SetSelectedPaymentMethodUserData
  ) {
    ctx.setState(
      produce((draft) => {
        draft.userData.billingStep.selectedPaymentMethodId = action.paymentMethodId;
      })
    );
  }

  @Action(SetDeductibleOverridesUserData)
  setDeductibleOverridesUserData(ctx: StateContext<OrderWizardDataStateModel>, action: SetDeductibleOverridesUserData) {
    ctx.setState(
      produce((draft) => {
        draft.userData.billingStep.deductibleOverrides = action.deductibleOverridesFormValues;
      })
    );
  }

  @Action(ValidateProviderVisit)
  validateProviderVisit(ctx: StateContext<OrderWizardDataStateModel>, action: ValidateProviderVisit) {
    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const state = ctx.getState();
    const selectedProviderId = state.userData.providerStep.selectedProviderId;
    const primaryInsuranceId = this.store.selectSnapshot(
      (state) => (state.patientInsurances as PatientInsurancesStateModel).insurance.data?.primaryInsurance?.id
    );
    const visitDate = action.visitDate;
    const initialTargetShipDate = OrderWizardDataState.initialTargetShipDate(state);
    const reorder = state.reorder.data;

    const stateKey = action.forLastVisit ? 'lastProviderVisitValidation' : 'nextProviderVisitValidation';

    let errors: string[] = [];

    if (!patientId) {
      errors = [...errors, 'Missing patient id'];
    }

    if (!primaryInsuranceId) {
      errors = [...errors, 'Missing primary insurance'];
    }

    if (!initialTargetShipDate) {
      errors = [...errors, 'Missing target ship date'];
    }

    if (!reorder) {
      errors = [...errors, 'Missing reorder'];
    }

    if (!reorder?.shipmentTypeId) {
      errors = [...errors, 'Missing shipment type id'];
    }

    if (!reorder?.items || reorder?.items.length === 0) {
      errors = [...errors, 'Missing reorder items'];
    }

    if (errors.length > 0) {
      const error = new MissingApiRequestDataError(errors);
      return this.handleError({ ctx, error, stateKey });
    }

    const requestId = uniqueId();

    ctx.setState(
      produce((draft) => {
        draft[stateKey].isLoading = true;
        draft[stateKey].requestId = requestId;
      })
    );

    const body = buildProviderVisitValidationRequest(
      patientId!,
      selectedProviderId === -1 ? null : selectedProviderId,
      primaryInsuranceId!,
      visitDate,
      initialTargetShipDate!,
      reorder!
    );

    return this.apiService.validateProviderVisit(patientId!, body).pipe(
      tap({
        next: (response) => {
          if (requestId !== ctx.getState()[stateKey].requestId) return;
          const data = mapProviderVisitValidationResponse(response.data, visitDate, initialTargetShipDate!);

          if (data === null) {
            const error = new InvalidApiResponseDataError(['Provider visit validation resulted in exception']);
            this.handleError({ ctx, error, stateKey });
          } else {
            this.handleSuccess({ ctx, data, stateKey });
            if (!action.forLastVisit) {
              this.store.dispatch(new RefetchCopayIfNeeded(action.previousTargetShipDate));
            }
          }
        },
      }),
      catchError(
        (error: Error): Observable<any> =>
          requestId === ctx.getState()[stateKey].requestId ? this.handleError({ ctx, error, stateKey }) : of(null)
      )
    );
  }

  @Action(SetOrderConfirmed)
  setOrderConfirmed(ctx: StateContext<OrderWizardDataStateModel>, action: SetOrderConfirmed) {
    ctx.setState(
      produce((draft) => {
        draft.userData.isOrderConfirmed = action.isConfirmed;
      })
    );
  }

  @Action(ResetOrderWizardData)
  reset(ctx: StateContext<OrderWizardDataStateModel>) {
    ctx.setState(
      produce((draft) => {
        Object.assign(draft, defaults);
      })
    );
  }
}
